import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { ThemeToggle } from '@/components/ThemeToggle';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const signIn = useAuthStore((s) => s.signIn);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      const e2 = err as { code?: string; message?: string; retryAfterSeconds?: number };
      const msg = (e2.message ?? '').toLowerCase();
      if (e2.code === 'LOGIN_LOCKED') {
        const minutes = Math.ceil((e2.retryAfterSeconds ?? 900) / 60);
        setError(`Demasiados intentos fallidos. Intenta de nuevo en ${minutes} minuto${minutes === 1 ? '' : 's'}.`);
      } else if (e2.code === 'CLIENT_USE_MOBILE_APP') {
        setError(
          'Este panel es solo para operadores. Si eres cliente, ingresa desde la app móvil ASI Security.'
        );
      } else if (
        e2.code === 'email_not_confirmed' ||
        msg.includes('email not confirmed')
      ) {
        setError(
          'Debes confirmar tu correo antes de iniciar sesión. Revisa tu bandeja de entrada.'
        );
      } else {
        setError('Credenciales inválidas. Intente de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative">
      {/* Theme toggle */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="bg-sidebar p-8 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Logo area */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo_dark.png"
            alt="Corporación ASI"
            className="h-20 w-auto object-contain"
          />
        </div>
        <p className="text-white/60 text-center mb-8">Centro de Monitoreo</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-white/80 mb-1">Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2.5 bg-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-secondary border border-white/10"
              placeholder="operador@corporacionasi.com"
            />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 pr-10 bg-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-secondary border border-white/10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/90 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
}
